import parse from "html-react-parser";
import { destinationsApi, staticApi, statisticsApi } from "src/services/api";
import { isLangAbsent, redirectWithLang } from "./utils/loader-utils";
import { STATIC_ELEMENT } from "src/constants/static";
import { nanoid } from "@reduxjs/toolkit";
import { buildLinkToCheapFlightsOfferPage } from "src/utils/query-utils";

export const HEADING_ID_PREFIX = "cheap-flights-booking-guide-";
const MAX_CHEAP_FLIGHTS = 9;

export const parseHeadingsFromHtml = (htmlContent, minLevel = 4, maxLevel = 4) => {
  // Extract headings during render (works in SSR)
  const headings = [];
  let count = 1;

  const options = {
    replace: (domNode) => {
      if (domNode.type === "tag" && /^h[1-6]$/.test(domNode.name)) {
        const level = parseInt(domNode.name.substring(1));
        if (level >= minLevel && level <= maxLevel) {
          const anchor =
            domNode.attribs?.id ||
            domNode.children[0]?.data
              ?.toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");

          if (anchor && domNode.children[0]?.data) {
            headings.push({
              anchor: HEADING_ID_PREFIX + count++,
              title: domNode.children[0].data,
              level,
            });
          }
        }
      }
    },
  };

  parse(htmlContent, options);
  return headings;
};

export async function loader({ request, params }) {
  if (isLangAbsent(request)) return redirectWithLang(request);
  const lang = new URL(request.url).searchParams.get("lang");

  const loaderData = {
    saleBanners: {
      data: Array.from({ length: 3 }, () => ({ id: nanoid(5), image: null })),
      error: false,
    },
    destinations: { data: [], error: false },
    selectedDeals: { data: [], error: false },
    topDestinations: { data: [], error: false },
    cheapDestinations: { data: [], error: false },
    bookingGuide: { data: { css: null, milestones: [], html: null }, error: false },
    footer: { data: null, error: false },
  };

  const blockingApiCalls = [];
  // load sale banners
  blockingApiCalls.push(
    staticApi.getStaticBanners(lang).then((res) => ({
      data: res.data.map((item) => ({ image: item, id: nanoid(5), link: "/" })),
    }))
  );

  // load top destinations
  blockingApiCalls.push(destinationsApi.getTravelDeals().catch(console.error));

  // load cached cheap flights
  blockingApiCalls.push(
    statisticsApi
      .getCheapFlights(
        "oneway",
        window.SERVER_DATA.REACT_APP_DEFAULT_ORIGIN_IATA,
        ...Array(5).fill(null),
        true
      )
      .catch(console.error)
  );

  const company = window.SERVER_DATA.REACT_APP_TENANT_NAME;
  //load static HTML file (booking process info/roadmap)
  blockingApiCalls.push(
    staticApi
      .getStaticFiles(company, lang, STATIC_ELEMENT.booking_guide)
      .then((res) => ({ data: { ...res, milestones: parseHeadingsFromHtml(res.html) } }))
  );

  await Promise.allSettled(blockingApiCalls)
    .then((results) => {
      results.forEach((result, i) => {
        const key = ["saleBanners", "selectedDeals", "destinations", "bookingGuide"][i];
        if (result.status === "rejected") {
          console.error(result.reason);
          loaderData[key].error = true;
        } else if (result.status === "fulfilled") {
          console.log(result.value);
          loaderData[key].data = result.value.data;
        }
      });
    })
    .catch(console.error);

  if (loaderData.selectedDeals.error) {
    loaderData.topDestinations.error = true;
  }
  if (loaderData.destinations.error) {
    loaderData.topDestinations.error = true;
    loaderData.cheapDestinations.error = true;
  }

  if (loaderData.destinations.data?.length) {
    const { topDestinations, cheapDestinations } = loaderData.destinations.data
      .map((item) => ({
        id: nanoid(5),
        city: item.dest.city,
        iata: item.dest.iata,
        price: item.price,
        link: buildLinkToCheapFlightsOfferPage("ow", item),
        direct: item.stopNum === 0,
      }))
      .reduce(
        (acc, item) => {
          let targetArr = acc.cheapDestinations;
          const matchingDeal = loaderData.selectedDeals.data.find((d) => d.iata === item.iata);
          if (matchingDeal) {
            targetArr = acc.topDestinations;
            item.category = matchingDeal.category;
          }

          if (!matchingDeal && targetArr.length >= MAX_CHEAP_FLIGHTS) return acc;

          const hasDuplicate = targetArr.some((i) => i.iata === item.iata);
          const cheaperNewItemIndex = targetArr.findIndex(
            (i) => i.iata === item.iata && i.price > item.price
          );

          if (cheaperNewItemIndex >= 0) {
            targetArr[cheaperNewItemIndex] = item;
          } else if (!hasDuplicate) {
            targetArr.push(item);
          }
          return acc;
        },
        { topDestinations: [], cheapDestinations: [] }
      );
    loaderData.topDestinations.data = topDestinations;
    loaderData.cheapDestinations.data = cheapDestinations;
  }
  delete loaderData.destinations;
  delete loaderData.selectedDeals;

  return loaderData;
}
